import { KVPrimitive } from 'types'

export interface PrimitiveGeometry {
  area?: number // in cm²
  volume?: number // in cm³
}

interface Scale {
  x: number
  y: number
  z: number
}

/**
 * Normalizes scale from either array format [x, y, z] or object format {x, y, z}.
 * Returns a consistent {x, y, z} object with fallback to 1 for missing values.
 */
function normalizeScale(
  scale: { x?: number; y?: number; z?: number } | number[] | undefined
): Scale {
  if (!scale) {
    return { x: 1, y: 1, z: 1 }
  }

  // Handle array format [x, y, z]
  if (Array.isArray(scale)) {
    return {
      x: scale[0] ?? 1,
      y: scale[1] ?? 1,
      z: scale[2] ?? 1,
    }
  }

  // Handle object format {x, y, z}
  return {
    x: scale.x ?? 1,
    y: scale.y ?? 1,
    z: scale.z ?? 1,
  }
}

/**
 * Calculates area and volume for a primitive based on its geometry type and dimensions.
 * Returns values in cm² and cm³ respectively, ready to be passed to formatArea/formatVolume.
 *
 * Conversion: raw dimensions × scale × measurementScale → cm
 *
 * @param primitive - The primitive to calculate geometry for
 * @param measurementScale - Conversion factor from Unreal units to cm
 * @returns Object with area (cm²) and volume (cm³), both optional
 */
export function calculatePrimitiveGeometry(
  primitive: KVPrimitive,
  measurementScale: number
): PrimitiveGeometry {
  // Normalize scale to handle both array [x,y,z] and object {x,y,z} formats
  const scale = normalizeScale(
    primitive.transform?.scale as
      | { x?: number; y?: number; z?: number }
      | number[]
      | undefined
  )

  switch (primitive.geometry) {
    case 'box': {
      if (!primitive.box_data) return {}
      const w = primitive.box_data.width * scale.x * measurementScale
      const d = primitive.box_data.depth * scale.y * measurementScale
      const h = primitive.box_data.height * scale.z * measurementScale
      return {
        area: 2 * (w * d + d * h + w * h),
        volume: w * d * h,
      }
    }
    case 'cylinder': {
      if (!primitive.cylinder_data) return {}
      const r =
        primitive.cylinder_data.radius *
        Math.max(scale.x, scale.y) *
        measurementScale
      const h = primitive.cylinder_data.height * scale.z * measurementScale
      return {
        area: 2 * Math.PI * r * (r + h),
        volume: Math.PI * r * r * h,
      }
    }
    case 'sphere': {
      if (!primitive.sphere_data) return {}
      const r =
        primitive.sphere_data.radius *
        Math.max(scale.x, scale.y, scale.z) *
        measurementScale
      return {
        area: 4 * Math.PI * r * r,
        volume: (4 / 3) * Math.PI * r * r * r,
      }
    }
    case 'plane': {
      if (!primitive.plane_data) return {}
      const w = primitive.plane_data.width * scale.x * measurementScale
      const h = primitive.plane_data.height * scale.y * measurementScale
      return {
        area: w * h,
        // No volume for a plane
      }
    }
    default:
      return {}
  }
}
